import ArticleLayout from '@/components/feature/ArticleLayout';

const pageData = {
  title: 'Ultimate AllChinaBuy Shopping Guide 2026',
  description: 'Your complete guide to shopping with the AllChinaBuy Spreadsheet. Learn how to find products, verify sellers, use QC guides, and ship internationally with confidence.',
  image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20photography%20of%20a%20clean%20desk%20workspace%20with%20laptop%20smartphone%20notebook%20and%20fashion%20items%20on%20cream%20white%20surface%2C%20soft%20natural%20lighting%2C%20editorial%20style%2C%20warm%20neutral%20tones&width=1200&height=600&seq=article-shopping-guide-2026&orientation=landscape',
  imageAlt: 'Ultimate AllChinaBuy Shopping Guide 2026',
  category: 'Guide',
  readTime: '15 min read',
  dateLabel: 'Updated June 2026',
  schemaData: {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Ultimate AllChinaBuy Shopping Guide 2026',
    description: 'Complete guide to shopping with the AllChinaBuy Spreadsheet. Learn to find products, verify sellers, use QC, and ship internationally.',
    datePublished: '2026-06-15',
    dateModified: '2026-06-28',
    author: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet.ink' },
    publisher: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet.ink' },
  },
};

export default function ShoppingGuidePage() {
  return (
    <ArticleLayout
      title={pageData.title}
      description={pageData.description}
      image={pageData.image}
      imageAlt={pageData.imageAlt}
      category={pageData.category}
      readTime={pageData.readTime}
      dateLabel={pageData.dateLabel}
      schemaData={pageData.schemaData}
    >
      <section>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
          Getting Started with AllChinaBuy Spreadsheet
        </h2>
        <p className="text-foreground-700 leading-relaxed">
          If you are new to the <strong>AllChinaBuy Spreadsheet</strong>, welcome to the smartest way to shop online. Our spreadsheet eliminates hours of searching by providing you with a curated directory of over 5,000 products from 300+ verified sellers across 14 categories. Rather than hunting through countless listings and hoping for the best, you can browse our organized spreadsheet and find exactly what you are looking for in minutes.
        </p>
        <p className="text-foreground-700 leading-relaxed mt-3">
          The AllChinaBuy Spreadsheet is updated daily by our team of experienced buyers who personally test products and verify sellers before adding them to the directory. This means every link you click leads to a legitimate product from a trusted source. No dead links, no sketchy sellers, no wasted time.
        </p>
      </section>

      <section>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
          How to Navigate the AllChinaBuy Spreadsheet Categories
        </h2>
        <p className="text-foreground-700 leading-relaxed">
          Our AllChinaBuy Spreadsheet is organized into 14 distinct categories: Shoes, Hoodies & Sweaters, T-Shirts, Jackets, Pants & Shorts, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and Other Stuff. Each category contains product links from verified sellers with detailed descriptions and pricing information.
        </p>
        <p className="text-foreground-700 leading-relaxed mt-3">
          We recommend starting with the category that interests you most. If you are into sneakers, the Shoes category has thousands of verified links to the latest releases. Into streetwear? The Hoodies, T-Shirts, and Jackets categories have you covered. Each product link opens directly on AllChinaBuy so you can view details, seller ratings, and place your order immediately.
        </p>
      </section>

      <section>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
          Understanding Seller Verification on AllChinaBuy Spreadsheet
        </h2>
        <p className="text-foreground-700 leading-relaxed">
          One of the biggest advantages of using our AllChinaBuy Spreadsheet is seller verification. Every seller in our directory has been vetted through a multi-step process that includes transaction history checks, buyer feedback analysis, and product quality verification through sample orders. Sellers who do not meet our standards are immediately removed.
        </p>
        <p className="text-foreground-700 leading-relaxed mt-3">
          This verification process sets our AllChinaBuy Spreadsheet apart from random links you might find on social media or forums. When you use our spreadsheet, you are shopping with sellers who have proven their reliability over time. We also regularly update seller ratings to reflect the most current information.
        </p>
      </section>

      <section>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
          Using QC Guides for Confident Purchases
        </h2>
        <p className="text-foreground-700 leading-relaxed">
          Quality Control guides are a core feature of our AllChinaBuy Spreadsheet. Before you ship any product, you should review the QC photos provided by the seller. Our guides teach you exactly what to look for in these photos: stitching quality, material accuracy, logo placement, color matching, and overall construction.
        </p>
        <p className="text-foreground-700 leading-relaxed mt-3">
          If QC photos reveal any issues, you can request better photos or cancel the order before it ships. This process saves you from receiving disappointing products and gives you complete control over your purchase. Our AllChinaBuy Spreadsheet includes QC checklists for every major category.
        </p>
      </section>

      <section>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
          Shipping Your AllChinaBuy Purchases Internationally
        </h2>
        <p className="text-foreground-700 leading-relaxed">
          Shipping is often the most intimidating part of shopping through AllChinaBuy, but our AllChinaBuy Spreadsheet makes it simple. We include shipping tutorials that explain different shipping lines, estimated delivery times, declaration tips, customs handling, and how to save on international delivery costs.
        </p>
        <p className="text-foreground-700 leading-relaxed mt-3">
          The key to successful shipping is choosing the right line for your package weight, destination, and urgency. Our guides help you make informed decisions so your items arrive safely and without unexpected fees. Many of our shipping tips come directly from experienced buyers who have shipped thousands of packages internationally.
        </p>
      </section>

      <section>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
          AllChinaBuy Spreadsheet FAQ
        </h2>
        <div className="space-y-3">
          {[
            { q: 'Is the AllChinaBuy Spreadsheet free?', a: 'Yes, completely free. We believe everyone deserves access to reliable product information without paying.' },
            { q: 'How do I know sellers are trustworthy?', a: 'Every seller is verified through transaction history, buyer feedback, and product quality testing before being added.' },
            { q: 'Can I request new categories?', a: 'Absolutely. Our team welcomes suggestions and regularly adds new product types based on user requests.' },
            { q: 'What if a link stops working?', a: 'Our team updates the spreadsheet daily and removes dead links within 24 hours of detection.' },
          ].map((faq) => (
            <details key={faq.q} className="group bg-background-50 rounded-xl border border-background-200/60 overflow-hidden">
              <summary className="flex items-center justify-between gap-4 px-5 py-4 cursor-pointer list-none">
                <h3 className="text-sm font-semibold text-foreground-900 pr-6">{faq.q}</h3>
                <div className="w-5 h-5 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 group-open:bg-primary-100 group-open:text-primary-600 transition-colors flex-shrink-0">
                  <i className="ri-add-line text-xs group-open:hidden"></i>
                  <i className="ri-subtract-line text-xs hidden group-open:block"></i>
                </div>
              </summary>
              <div className="px-5 pb-4 -mt-1">
                <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
              </div>
            </details>
          ))}
        </div>
      </section>
    </ArticleLayout>
  );
}